import { createContext, useCallback, useMemo, useState } from 'react';

import { INotificationSettingsConfirmationProps } from './NotificationSettingsConfirmation';

export const useNotificationSettingsConfirmation = () => {
  const [confirmationTitle, setConfirmationTitle] = useState<string | undefined>(undefined);
  const [confirmationSubText, setConfirmationSubText] = useState<string | undefined>(undefined);
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | undefined>();

  // Exits confirmation panel by setting all properties to undefined
  const exitConfirmationPanel = useCallback(() => {
    setConfirmationTitle(undefined);
    setConfirmationSubText(undefined);
    setOnConfirmAction(undefined);
  }, [setConfirmationTitle, setConfirmationSubText, setOnConfirmAction]);

  // Shows the confirmation panel by setting all required properties
  const showConfirmationPanel = useCallback(
    (title: string, subText: string, onConfirm: () => void) => {
      setConfirmationTitle(title);
      setConfirmationSubText(subText);
      setOnConfirmAction(() => onConfirm); // passing in the function directly will envoke it
    },
    [setConfirmationTitle, setConfirmationSubText, setOnConfirmAction]
  );

  // Confirmation props to be passed to the confirmation panel, undefined when it is not visible
  const confirmationProps = useMemo(
    (): INotificationSettingsConfirmationProps | undefined =>
      !!confirmationTitle && !!confirmationSubText && !!onConfirmAction
        ? {
            title: confirmationTitle,
            subText: confirmationSubText,
            onClose: exitConfirmationPanel,
            onConfirm: () => {
              onConfirmAction();
              exitConfirmationPanel();
            },
          }
        : undefined,
    [confirmationTitle, confirmationSubText, exitConfirmationPanel, onConfirmAction]
  );

  return {
    confirmationProps,
    showConfirmationPanel,
    exitConfirmationPanel,
  };
};

interface INotificationSettingsConfirmationContext {
  showConfirmationPanel: (title: string, subText: string, onConfirm: () => void) => void;
}

export const NotificationSettingsConfirmationContext = createContext<INotificationSettingsConfirmationContext>({
  showConfirmationPanel: () => {},
});
